import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { shareReplay, share } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './core/api.service';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { UtilitiesService } from './core/utilities.service';

@Injectable({ providedIn: 'root' })
export class StoreService {
  private api_url: string;
  private api_url2: string;
  // private store_url: string;

  private _plans = null;

  private subject = new Subject<any>();

  private httpClient: HttpClient;

  /** Guards against one expired token raising a notice per in-flight request. */
  private handlingUnauthorized = false;

  /**
   * Returns a custom HttpClient object with interceptors for error handling.
   *
   * @return {Object} An object with methods for making HTTP requests: post, get, put, and delete.
   */
  private get customHttpClient() {
    // Intercept http calls
    const createHttpMethod = (method) => {
      return (path, bodyOrOptions?, options?) => {
        const args =
          method === 'post' || method === 'put'
            ? [path, bodyOrOptions, options]
            : [path, bodyOrOptions];

        const httpClient = this.httpClient[method](...args).pipe(share());

        httpClient.subscribe(() => {
          // A call got through, so the session is good again and the next
          // expiry is allowed to raise its own notice.
          this.handlingUnauthorized = false;
        }, handleError);
        return httpClient;
      };
    };

    const handleError = (response) => {
      const error = response?.error;

      if (+response?.status !== 401) return;

      // A page loads through several calls at once, so one expired token fails
      // all of them. Only the first gets to speak: the rest would each stack
      // another modal, and closing one would just uncover the next.
      if (this.handlingUnauthorized) return;
      this.handlingUnauthorized = true;

      const malformedToken = [
        'Maximum stack depth exceeded',
        'Invalid or malformed JSON',
        'Unexpected control character found',
        'Syntax error, malformed JSON',
        'Malformed UTF-8 characters',
      ].includes(error?.error);

      this.utils.msgBox.open({
        message: this.utils.translate(
          malformedToken
            ? 'Invalid session token. Please refresh or log in again.'
            : 'Your session has expired. Please log in again.'
        ),
        title: this.utils.translate('Error'),
        icon: 'error',
        contentClassList: 'whitespace-pre-line',
      });

      // 401 means the request was not authenticated, whatever the wording, so
      // every case clears the session and lands on /login. false suppresses the
      // toast, since the modal above already says it.
      this.utils.errorUnauthorized(false);
    };

    return {
      post: createHttpMethod('post'),
      get: createHttpMethod('get'),
      put: createHttpMethod('put'),
      delete: createHttpMethod('delete'),
    };
  }

  constructor(
    private apiSvc: ApiService,
    private http: HttpClient,
    private utils: UtilitiesService,
    backend: HttpBackend
  ) {
    //this.api_url = '';//environment.store;
    this.api_url = environment.store;
    this.api_url2 = environment.store2;
    // this.store_url = environment.store_url;

    this.httpClient = new HttpClient(backend);
  }

  public publish_event(body) {
    const path = `${this.api_url}/push_event`;

    return this.customHttpClient.post(path, body);
  }

  /**
   * Sends the Mixpanel "AI Generate Success" / "AI Generate Fail" event for AI buttons.
   * Fire-and-forget: tracking problems are logged and never affect the AI feature.
   */
  trackAiGenerateEvent(
    event: 'AI Generate Success' | 'AI Generate Fail',
    props: { aiPurpose: string; storeId?: string | number; token?: number; errorResponse?: string }
  ) {
    try {
      const info = this.userInfo;
      const dealerId = info.d_id || info.dealer_id || '';
      const data: any = {
        d_id: dealerId,
        type: event,
        distinct_id: dealerId,
        dealer_username: info.username || info.dealer_name || '',
        platform: 'dashboard',
        store_id: props.storeId || localStorage.getItem('store_id') || '',
        page: window.location.pathname,
        ai_purpose: props.aiPurpose,
      };

      // Only report tokens when credits were actually deducted.
      // Not named "token": Mixpanel reserves that for the project token and drops the event.
      if (event === 'AI Generate Success' && props.token > 0) {
        data.token_deducted = props.token;
      }
      if (event === 'AI Generate Fail') {
        data.error_response = props.errorResponse || '';
      }

      this.publish_event({ data, application: 'dashboard', driver: 'mixpanel' }).subscribe({
        error: (err) => console.log('[Mixpanel] AI event tracking error:', err),
      });
    } catch (e) {
      console.log('[Mixpanel] AI event tracking error:', e);
    }
  }

  get userInfo() {
    const sessionInfo = JSON.parse(localStorage.getItem('5s-info'));
    return sessionInfo || {};
  }

  SSOVerify(token, redirect, fromStore) {
    const path = `${this.api_url}/user/verify`;

    const options: any = {
      params: {
        auth: token,
        redirect_to: redirect,
        from_store: fromStore,
      },
    };

    return this.apiSvc.post(path, null, options);
  }

  login(params) {
    const path = `${this.api_url}/user/login`;
    const { username, password } = params;

    const formData = new FormData();

    formData.append('username', username);
    formData.append('password', password);

    return this.apiSvc.post(path, formData);
  }

  getProfile(d_id) {
    const path = `${this.api_url}/profile/my_pid`;

    const params: any = {
      d_id: d_id,
    };

    // return this.apiSvc.get(path, params)
    return this.apiSvc.get2(path, {});
  }

  getCustomerInfo(store_id) {
    const path = `${this.api_url}/estore/customer_details`;
    const { fullname, email } = this.userInfo;

    const params: any = {
      // name: fullname,
      // email: email,
      store_id: store_id,
    };

    return this.apiSvc.get(path, params);
  }

  getStoreList(profileId) {
    const path = `${this.api_url}/estore/store_list`;

    // const params:any = {
    //   profile_id: profileId
    // };

    return this.apiSvc.get(path);
  }

  getStorePreferences(params) {
    const path = `${this.api_url}/estore/update/get_preference`;
    return this.apiSvc.get(path, params);
  }

  getPlans() {
    if (this._plans) {
      return of(this._plans);
    }

    const path = `${this.api_url}/estore/plans`;

    return this.apiSvc.get(path).pipe(map((res) => (this._plans = res)));
  }

  getSalesInfo(store_id) {
    const path = `${this.api_url}/estore/report/total_sales`;

    const params: any = {
      store_id: store_id,
    };

    return this.apiSvc.get(path, params);
  }

  getRecentOrders(store_id) {
    const path = `${this.api_url}/estore/report/recent_order`;

    const params: any = {
      store_id: store_id,
    };

    return this.apiSvc.get(path, params);
  }

  getTotalOrders(store_id) {
    const path = `${this.api_url}/estore/report/total_order`;

    const params: any = {
      store_id: store_id,
    };

    return this.apiSvc.get(path, params);
  }

  encrypt(dealerId) {
    const path = `${this.api_url}/estore/encrypt_dealer`;

    const formData = new FormData();

    formData.append('d_id', dealerId);

    return this.apiSvc.post(path, formData);
  }

  decrypt(dealerId) {
    const path = `${this.api_url}/estore/decrypt_dealer`;

    const formData = new FormData();

    formData.append('d_id', dealerId);

    return this.apiSvc.post(path, formData);
  }

  suspendStore(store_id) {
    const path = `${this.api_url}/estore/suspend_store`;

    const options = {
      params: { store_id },
    };

    return this.apiSvc.post(path, null, options);
  }

  resumeStore(store_id) {
    const path = `${this.api_url}/estore/resume_store`;

    const options = {
      params: { store_id },
    };

    return this.apiSvc.post(path, null, options);
  }

  getFAQ() {
    const path = `${this.api_url}/estore/store_faqs`;

    return this.apiSvc.get(path);
  }

  getFAQ2(token, category) {
    const path = `${this.api_url}/faq/list?platform=dashboard&language_code=en-US&category=${category}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getLanguages() {
    const path = `${this.api_url}/estore/store_languages`;

    return this.apiSvc.get(path);
  }

  updateLanguage(params) {
    const path = `${this.api_url}/estore/store_update_languages`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('d_id', params.dealer_id);
    formData.append('language', params.language_code);

    return this.apiSvc.post(path, formData);
  }

  deleteDomain(params) {
    const path = `${this.api_url}/domain/delete`;

    const store_id = localStorage.getItem('store_id');

    const formData = new FormData();

    formData.append('store_id', store_id);
    formData.append('store_domain_id', params.store_domain_id);

    return this.apiSvc.post(path, formData);
  }

  validateDomain(token, params) {
    const path = `${this.api_url}/domain/available?domain=` + params.domain;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getRestrictedSubdomain() {
    const token = localStorage.getItem('bearer');
    const path = `${this.api_url}/domain/reserved_names`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  validateSubDomain(token, params) {
    const path =
      `${this.api_url}/domain/check_available_subdomain?subdomain=` +
      params.subDomain;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  suggestAvailableSubdomain(token, params) {
    const path =
      `${this.api_url}/domain/suggest_available_subdomain?subdomain=` +
      params.subdomain;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  suggestDomain(token, params) {
    const path = `${this.api_url}/domain/suggest?domain=` + params.domain;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getStoreProfile(storeId) {
    const path = `${this.api_url}/estore/store_profile`;

    const options: any = {
      store_id: storeId,
    };

    return this.apiSvc.get(path, options);
  }

  saveCompanyInfo(params) {
    const path = `${this.api_url}/estore/update/company_name_email`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('company_name', params.company_name);
    formData.append('company_email', params.company_email);

    return this.apiSvc.post(path, formData);
  }

  getStoreTemplates() {
    const path = `${this.api_url}/estore/store_templates`;

    return this.apiSvc.get(path);
  }

  getCountries() {
    // const path = `/assets/data/countries.json`;
    const path = `${this.api_url}/common/country_list`;

    const options: any = {
      include_other_country: 1,
    };

    return this.apiSvc.get(path, options);
  }

  getStates(country) {
    const path = `/assets/data/malaysia-states.json`;

    return this.apiSvc.get(path);
  }

  getUnits() {
    const path = `/assets/data/units.json`;

    return this.apiSvc.get(path);
  }

  getCurrencies() {
    const path = `/assets/data/currencies.json`;

    return this.apiSvc.get(path);
  }

  getCurrencyFormats() {
    const path = `/assets/data/currency_format.json`;

    return this.apiSvc.get(path);
  }

  getTimezones() {
    const path = `/assets/data/timezones.json`;

    return this.apiSvc.get(path);
  }

  getTimeFormats() {
    const path = `/assets/data/time-display_format.json`;

    return this.apiSvc.get(path);
  }

  getDateFormats() {
    const path = `/assets/data/date-display_format.json`;

    return this.apiSvc.get(path);
  }

  getProductSortOrder() {
    const path = `/assets/data/product-sort-order.json`;

    return this.apiSvc.get(path);
  }

  saveCompanyAddress(params) {
    const path = `${this.api_url}/estore/update/company_address`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('street', params.street);
    formData.append('city', params.city);
    formData.append('country', params.country);
    formData.append('zipcode', params.zipcode);
    formData.append('state', params.state);
    formData.append('phone', params.phone);

    return this.apiSvc.post(path, formData);
  }

  validateEmailAddress(params) {
    const path = `${this.api_url}/estore/profile/init`;

    // const options:any = {
    //   params: {
    //     profile_email: params.email
    //   }
    // };

    // return this.apiSvc.get2(path, options);

    const formData = new FormData();

    formData.append('profile_email', params.email);

    return this.apiSvc.post(path, formData);
  }

  createStore(token, params) {
    //const path = `${this.api_url}/estore/create_store`;

    //const location = `${window.location.origin}${window.location.hash.indexOf('#') !== -1 ? '/#' : ''}`;
    /*
        const options = {
          params: {
            redirect_url: `${location}/thank-you`,
            cancel_url: `${location}/payment-failed`
          }
        };

        const formData = new FormData();

        //formData.append('store_name', params.name);
        //formData.append('d_id', params.d_id);
        //formData.append('d_id', '2001');
        formData.append('store_plan_id', params.planId);
        formData.append('store_url', params.subDomain);
        formData.append('theme_id', params.templateId);
          formData.append('language', params.language);
          formData.append('country', params.country);
    */
    //return this.apiSvc.post(path, formData, options);

    const path = `${this.api_url}/estore/create`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, params, { headers });
  }

  createMultiStore(token, params) {
    const path = `${this.api_url}/estore/create_multi`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, params, { headers });
  }

  setCnameAsDone(token, params) {
    const formData = new FormData();
    formData.append('store_id', params.storeId);
    formData.append('domain', params.customDomain);

    const path = `${this.api_url}/domain/manual_cname_done`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, formData, { headers });
  }

  /**
   * Delete a subdomain from a domain.
   * Required payload: store_id, store_domain_id, subdomain
   */
  deleteSubdomain(
    token,
    params: { store_id: string | number; store_domain_id: string | number; subdomain: string }
  ) {
    const formData = new FormData();
    formData.append('store_id', String(params.store_id));
    formData.append('store_domain_id', String(params.store_domain_id));
    formData.append('subdomain', params.subdomain);

    const path = `${this.api_url}/domain/delete_subdomain`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, formData, { headers });
  }

  requestVerifyDomain(token, params) {
    const formData = new FormData();
    formData.append('store_id', params.storeId);
    formData.append('store_domain_id', params.store_domain_id);
    if (params.subdomain) {
      formData.append('subdomain', params.subdomain);
    }

    const path = `${this.api_url}/domain/verify_cname`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, formData, { headers });
  }

  createCustomDomain(token, params) {
    return;
    const formData = new FormData();
    formData.append('store_id', params.storeId);
    formData.append('domain', params.customDomain);

    const path = `${this.api_url}/store_domain/create`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, formData, { headers });
  }

  createExistingCustomDomain(token, params) {
    const formData = new FormData();
    formData.append('store_id', params.storeId);
    formData.append('domain', params.customDomain);

    const path = `${this.api_url}/store_domain/add_existing_domain`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, formData, { headers });
  }

  updateTracking(params) {
    const path = `${this.api_url}/estore/update/tracking_analytics`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('google_id', params.gaId);
    formData.append('facebook_id', params.pixelId);

    return this.apiSvc.post(path, formData);
  }

  updateStoreName(params) {
    const path = `${this.api_url}/estore/update/store_name`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('store_name', params.storeName);
    formData.append('store_url', params.storeName);

    return this.apiSvc.post1(path, formData);
  }

  updateSubDomain(params) {
    const path = `${this.api_url}/estore/update/sub_domain`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('sub_domain', params.subdomain);

    return this.apiSvc.post1(path, formData);
  }

  updateUnits(params) {
    const path = `${this.api_url}/estore/update/weight_size`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('weight_unit', params.weight_unit);
    formData.append('dimension_unit', params.dimension_unit);

    return this.apiSvc.post(path, formData);
  }

  updateProfile(params) {
    const path = `${this.api_url}/estore/update/profile_info`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('name', params.fullname);
    // formData.append('nickname', params.nickname);
    formData.append('email', params.email);

    return this.apiSvc.post(path, formData);
  }

  getInvoiceList() {
    const path = `${this.api_url}/estore/subscription/invoices`;

    return this.apiSvc.get(path);
  }

  getPdfInvoice(invoiceId) {
    const path = `${this.api_url}/estore/subscription/invoice/pdf`;

    const options: any = {
      params: {
        invoice_id: invoiceId,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  updateCurrency(params) {
    const path = `${this.api_url}/estore/update/currency`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('currency', params.code);
    formData.append('group_separator', params.separator);

    return this.apiSvc.post(path, formData);
  }

  updateDateTime(params) {
    const path = `${this.api_url}/estore/update/date_time`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('date_format', params.dateFormat);
    formData.append('time_format', params.timeFormat);
    formData.append('time_zone', params.timezone);

    return this.apiSvc.post(path, formData);
  }

  updateStorePreference(params) {
    const path = `${this.api_url}/estore/update/update_preference`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('preferences_json', params.preferences_json);

    return this.apiSvc.post(path, formData);
  }

  updateCatalogSettings(params) {
    const path = `${this.api_url}/estore/update/catalog_setting`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('hide_stock', params.hideStock);
    formData.append('product_sort', params.sortOrder);

    return this.apiSvc.post(path, formData);
  }

  updateOrderCommentsSettings(params) {
    const path = `${this.api_url}/estore/update/order_comments`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('order_notes', params.orderNotes);
    formData.append('order_caption', params.caption);
    formData.append('order_require', params.orderRequire);

    return this.apiSvc.post(path, formData);
  }

  updateOrderIdSettings(params) {
    const path = `${this.api_url}/estore/update/order_id`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('prefix', params.prefix);
    formData.append('suffix', params.suffix);

    return this.apiSvc.post(path, formData);
  }

  getOrdersInfo(storeId) {
    const path = `${this.api_url}/estore/order_details`;

    const options: any = {
      params: {
        store_id: storeId,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  getProducts(storeId) {
    const path = `${this.api_url}/estore/product/manager_products`;

    const options: any = {
      params: {
        store_id: storeId,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  toggleProductDisplay(storeId, statusInfo) {
    const path = `${this.api_url}/estore/product/manager/update_display_status`;

    const options: any = {
      params: {
        store_id: storeId,
        show_product: JSON.stringify(statusInfo),
      },
    };

    return this.apiSvc.post(path, null, options);
  }

  getProductDetails(storeId, pId) {
    const path = `${this.api_url}/estore/product/manager/custom_details`;

    const options: any = {
      params: {
        store_id: storeId,
        p_id: pId,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  saveProductDetails(params) {
    const path = `${this.api_url}/estore/product/manager/custom_details`;

    const formData = new FormData();

    formData.append('store_id', params.storeId);
    formData.append('p_id', params.pId);
    formData.append('product_desc', params.desc);
    formData.append('product_shortdesc', params.shortdesc);
    formData.append('product_name', params.name);

    return this.apiSvc.post(path, formData);
  }

  updateSocialMediaLinks(params) {
    const path = `${this.api_url}/estore/update/social`;

    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('facebook', params.facebook);
    formData.append('instagram', params.instagram);
    formData.append('twitter', params.twitter);
    formData.append('youtube', params.youtube);
    formData.append('pinterest', params.pinterest);

    return this.apiSvc.post(path, formData);
  }

  getNotifications(storeId, indicator = 0) {
    const path = `${this.api_url}/estore/notification/list`;

    const options: any = {
      params: {
        store_id: storeId,
        indicator: indicator,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  updateNotificationItem(itemId) {
    const path = `${this.api_url}/estore/notification/update`;

    const formData = new FormData();

    formData.append('notification_id', itemId);

    return this.apiSvc.post(path, formData);
  }

  markAllNotificationsAsRead(itemIds) {
    const path = `${this.api_url}/estore/notification/markall`;

    const formData = new FormData();

    formData.append('notification_id', itemIds);

    return this.apiSvc.post(path, formData);
  }

  verifyPayment(id) {
    const path = `${this.api_url}/estore/checkout/verify_payment`;

    const formData = new FormData();

    formData.append('checkout_id', id);

    return this.apiSvc.post(path, formData);
  }

  checkDomainAvailability(domain) {
    const path = `${this.api_url}/webhook-ecom/v1/domain_status`;

    const options: any = {
      params: {
        domain: domain,
      },
    };

    return this.apiSvc.get2(path, options);
  }

  checkBatchRequestStatus(param) {
    const path = `${this.api_url}/estore/product/check_batch_status`;

    // const options: any = {
    //   params: {
    //     store_id: store_id
    //   }
    // };

    return this.apiSvc.get(path, param);
  }

  updateBatchRequestStatus(params) {
    const path = `${this.api_url}/estore/product/update_batch_status`;

    return this.apiSvc.post(path, params);
  }

  uploadProductImageBatches(params) {
    const path = `${this.api_url}/estore/product/upload_product_image`;

    // const options: any = {
    //   params: {
    //     store_id: store_id
    //   }
    // };

    return this.apiSvc.post(path, params);
  }

  getTemplateSchema(template_id: any) {
    const path = `${this.api_url}/estore/template/schema?template_id=${template_id}`;

    return this.apiSvc.get(path);
  }

  getTemplateData(store_id: any, template_id: any) {
    const path = `${this.api_url}/estore/template/data_value?store_id=${store_id}&template_id=${template_id}`;

    return this.apiSvc.get(path);
  }

  updateTemplateData(data: any, store_id: any, template_id: any) {
    const path = `${this.api_url}/estore/template/data_value?store_id=${store_id}&template_id=${template_id}`;

    return this.apiSvc.post(path, data);
  }

  sendClickEvent() {
    this.subject.next();
  }

  getClickEvent(): Observable<any> {
    return this.subject.asObservable();
  }

  // promotions
  getPromotions(store_id: any) {
    const path = `${this.api_url}/estore/promotion/list?store_id=${store_id}`;

    return this.apiSvc.get(path);
  }

  getProductList(country_code: any) {
    const path = `${this.api_url}/estore/product/listing?country_code=${country_code}`;

    return this.apiSvc.get(path);
  }

  getVoucherList(country_code: any) {
    const path = `${this.api_url}/estore/product/vouchers?country_code=${country_code}`;

    return this.apiSvc.get(path);
  }

  createPromotion(body: any) {
    const path = `${this.api_url}/estore/promotion/create-promotion`;

    return this.apiSvc.post(path, body);
  }

  updatePromotion(body: any) {
    const path = `${this.api_url}/estore/promotion/update-promotion`;

    return this.apiSvc.post(path, body);
  }

  deletePromotion(id: any) {
    const path = `${this.api_url}/estore/promotion/delete-promotion`;

    return this.apiSvc.post(path, id);
  }

  getLoginLink(body) {
    const path = `${this.api_url}/user/estore_login`;

    return this.apiSvc.post(path, body);
  }
  generateQR(body) {
    const path = `${this.api_url}/tools/generate_qr`;

    return this.apiSvc.get(path, body);
  }

  getLoginQR() {
    const path = `${this.api_url}/user/login_qr`;

    return this.apiSvc.get(path);
  }

  getRedirectLogin(id) {
    const path = `${this.api_url}/user/login/redirect?id=${id}`;

    return this.apiSvc.get(path);
  }

  // partners integration

  signOn(token) {
    const path = `${this.api_url}/estore/sign_on`;

    return this.apiSvc.post1(path, { token: token });
  }

  getStoreLists(token) {
    const path = `${this.api_url}/estore/store_list?store_status=1`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getCountryLists(token) {
    const path = `${this.api_url}/country/all?country_status=1`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getLanguageLists(token) {
    const path = `${this.api_url}/language/all?language_status=1`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  updateStatus(body, token) {
    const path = `${this.api_url}/estore/update_status`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getCustomerList(token, store_id, mobile: any, email: any, date, page, limit, country_code: any = '', segment: any = 'ALL', sort: string = '', order: string = '') {
    const path = `${this.api_url}/customer/list_customer?store_id=${store_id}&date=${date}&mobile=${mobile || ''}&email=${email || ''}&country_code=${country_code || ''}&segment=${segment || 'ALL'}&page=${page}&limit=${limit}&sort=${sort || ''}&order=${order || ''}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getCustomerDetail(token, store_id, mobile?: any, email?: any, customer_id?: any, mobile_prefix?: any) {
    const path = `${this.api_url}/customer/detail?store_id=${store_id}&mobile=${mobile || ''}&mobile_prefix=${mobile_prefix || ''}&email=${email || ''}&customer_id=${customer_id || ''}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  exportCustomerList(token, body) {
    const path = `${this.api_url}/customer/export?data=${body}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  downloadRequest(token, url, param) {
    const params = new URLSearchParams(param);
    const fullUrl = this.api_url + url + '?' + params;

    const viewFile = async (fullUrl) => {
      let filename = '';

      fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: `${token}`,
        },
      })
        .then((response) => {
          const header = response.headers.get('Content-Disposition');
          if (header) {
            const parts = header.split(';');
            filename = parts[1].split('=')[1];
            filename = filename.replace(/"/g, '');
          }
          return response.blob();
        })
        .then((blob) => {
          const _url = window.URL.createObjectURL(blob);
          let anchor = document.createElement('a');
          anchor.download = filename;
          anchor.href = _url;
          anchor.click();
        })
        .catch((err) => {
          console.log(err);
          //print error here
        });
    };
    viewFile(fullUrl);
  }

  /**
   * downloadRequest saves whatever comes back. The gateway answers an unrouted
   * path with 200 and the SPA's HTML, so a status check alone would still hand
   * the dealer a spreadsheet full of markup — the content type has to be
   * checked too. Rejects instead of saving when the body is not a file.
   */
  async downloadSpreadsheet(token, url, param) {
    const params = new URLSearchParams(param);
    const response = await fetch(`${this.api_url}${url}?${params}`, {
      method: 'GET',
      headers: { Authorization: `${token}` },
    });

    if (!response.ok) {
      throw new Error(`${url} responded ${response.status}`);
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (/text\/html|application\/json/i.test(contentType)) {
      throw new Error(
        `${url} returned ${contentType || 'no content type'} instead of a file — the endpoint is probably not routed`
      );
    }

    // filename="x.xlsx" or RFC 5987 filename*=UTF-8''x.xlsx, in any parameter order.
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1].trim()) : 'export.xlsx';

    const href = window.URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.download = filename;
    anchor.href = href;
    anchor.click();
    window.URL.revokeObjectURL(href);
  }

  getDomainList(token, store_id) {
    const path = `${this.api_url}/store_domain/list?store_id=${store_id}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getAIGeneratedContent(params = {}) {
    const path = `${this.api_url}/ai/generate_content`;
    const token = localStorage.getItem('bearer');
    const headers = new HttpHeaders().set('Authorization', token);
    const store_id = localStorage.getItem('store_id');

    params['store_id'] = store_id;

    return this.customHttpClient.get(path, { headers, params });
  }

  getAITemplateDetails(params = {}) {
    const path = `${this.api_url}/ai/prompt_template/details`;
    const token = localStorage.getItem('bearer');
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers, params });
  }

  getSubscription(
    token,
    store_id,
    start_date,
    end_date,
    page,
    limit,
    date_type
  ) {
    const path = `${this.api_url}/order/subscription/list?store_id=${store_id}&start_date=${start_date}&end_date=${end_date}&page=${page}&limit=${limit}&date_type=${date_type}&status=0`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getSubscriptionSearch(
    token,
    store_id,
    start_date,
    end_date,
    page,
    limit,
    search
  ) {
    const path = `${this.api_url}/order/subscription/list?store_id=${store_id}&start_date=${start_date}&end_date=${end_date}&page=${page}&limit=${limit}&search=${search}&status=0`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getSubscriptionDetail(token, store_id, id) {
    const path = `${this.api_url}/order/subscription/detail?id=${id}&store_id=${store_id}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  updateCartStatus(body, token) {
    const path = `${this.api_url}/cart/updateStatus`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, body, { headers });
  }

  getProductLists(token, store_id: any, language_code: any) {
    const path = `${this.api_url}/product/list?store_id=${store_id}&language_code=${language_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getProductSubscriptionList(token, d_id: any, country_code: any) {
    const path = `${this.api_url}/product/subscription/list?d_id=${d_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);
    
    return this.customHttpClient.get(path, { headers });
  }

  getTWSubscriptionStatus(token) {
    const path = `${this.api_url}/checkout_link/subscription_setting`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getFeedbackTopic(token) {
    const path = `${this.api_url}/feedback_topic/list`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getFeedbackList(token) {
    const path = `${this.api_url}/feedback/list`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getFeedbackInfo(token, feedback_id: any) {
    const path = `${this.api_url}/feedback/info?feedback_id=${feedback_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  createFeedback(topic_id: any, message: any, photo: any, token) {
    const path = `${this.api_url}/feedback/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { topic_id: topic_id, message: message, photo: photo },
      { headers }
    );
  }

  createAiSummaryFeedback(params: { rating: 'helpful' | 'not_helpful'; comment?: string; session_id?: string; store_id?: string }) {
    const path = `${this.api_url}/feedback/ai_summary`;
    const token = localStorage.getItem('bearer');
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, params, { headers });
  }

  getStoreConfig(token, store_id, config_name) {
    const path = `${this.api_url}/estore/config/get?store_id=${store_id}&name=${config_name}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getAllStoreConfig(token, store_id) {
    const path = `${this.api_url}/estore/config/all?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateConfig(token, body) {
    const path = `${this.api_url}/estore/config/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Sets or clears one pixel's Conversions API token. Separate from
  // updateConfig because the token must not travel inside facebook_pixel,
  // which is served publicly; the server merges it into its own config.
  updatePixelCapiToken(token, body) {
    const path = `${this.api_url}/estore/config/pixel_capi_token`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Reads one pixel's stored Conversions API token back in the clear. The only
  // call that returns it -- everything else treats tokens as write-only, since
  // the storefront config endpoint is public. Fired from an explicit "Show
  // token" action, never on load, so the credential is not sitting in the page
  // for anyone who happens to be looking at the screen.
  revealPixelCapiToken(token, body) {
    const path = `${this.api_url}/estore/config/pixel_capi_token/reveal`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // The dealer's Meta pixels and ad accounts, via nexus. Returns
  // eligible:false with a reason and sso_link when Facebook is not connected.
  getPixelList(token, body) {
    const path = `${this.api_url}/estore/config/pixel_list`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Checks a pixel id + CAPI token pair against Meta before saving. The token
  // is sent for checking only -- this does not store it.
  verifyPixelCapiToken(token, body) {
    const path = `${this.api_url}/estore/config/verify_pixel_capi_token`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Creates a new Meta pixel on one of the dealer's ad accounts.
  createPixel(token, body) {
    const path = `${this.api_url}/estore/config/pixel_create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Sends a manual Facebook Pixel / Conversions API event to Meta.
  triggerPixelEvent(token, body) {
    const path = `${this.api_url}/estore/config/trigger_pixel_event`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // Gets storefront orders with CAPI delivery status for the manual event modal.
  getStorefrontOrders(token, body) {
    const path = `${this.api_url}/estore/config/storefront_orders`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updateCheckoutStepsConfig(token, body) {
    const path = `${this.api_url}/estore/config/update_checkout_steps`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getDealerConfig(token, store_id, config_name) {
    const path = `${this.api_url}/dealer/config/get?dealer_id=${store_id}&name=${config_name}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getAllDealerConfig(token, store_id) {
    const path = `${this.api_url}/dealer/config/all?dealer_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateDealerConfig(token, body) {
    const path = `${this.api_url}/dealer/config/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updatePreferencesStoreLanguage(body, token, type) {
    const path =
      type == 'default'
        ? `${this.api_url}/preference/language/update_default`
        : `${this.api_url}/preference/language/update_status`;

    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, body, { headers });
  }

  updatePreferencesStoreCountry(body, token, type) {
    const path =
      type == 'default'
        ? `${this.api_url}/preference/country/update_default`
        : `${this.api_url}/preference/country/update_status`;

    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(path, body, { headers });
  }

  getCountry(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/preference/country/list?store_id=` + store_id;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getCountryWithOrder(token) {
    const store_id = localStorage.getItem('store_id');
    const path =
      `${this.api_url}/order/country_with_order?store_id=` + store_id;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getLatestSummary(token, storeId: any) {
    const path = `${this.api_url}/report/store/process?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getStoreInfo(token, store_id: any) {
    const path = `${this.api_url}/estore/info?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getGenerateContentQueueStatus(token, store_id: any) {
    const path = `${this.api_url}/estore/generate_content_queue/status?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getDealerPlan(token, dealerId: any) {
    const path = `${this.api_url}/dealer/plan?dealer_id=${dealerId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getPaymentMethod(token, storeId) {
    const path = `${this.api_url}/payment_method/list?status=1&store_id=${storeId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateDefaultStore(body, token) {
    const path = `${this.api_url}/dealer/default_store`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updateStore(
    store_id: any,
    store_name: any,
    store_url: any,
    store_logo: any,
    store_social_link: any,
    token
  ) {
    const path = `${this.api_url}/estore/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_name: store_name,
        store_url: store_url,
        store_id: store_id,
        store_logo: store_logo,
        store_social_link: store_social_link,
      },
      { headers }
    );
  }

  updateStoreDetails(store_id: any, store_name: any, store_url: any, token) {
    const path = `${this.api_url}/estore/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_name: store_name,
        store_url: store_url,
        store_id: store_id,
      },
      { headers }
    );
  }

  updateWhatsapp(store_id: any, store_social_link: any, token) {
    const path = `${this.api_url}/estore/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        store_social_link: store_social_link,
      },
      { headers }
    );
  }

  updateDisplayStatus(body, token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/product_custom/update_display_status`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updateProductCustom(
    store_id: any,
    product_id: any,
    language_code: any,
    product_name: any,
    product_desc: any,
    product_overview: any,
    primary_photo: any,
    imgURL_UploadImg: any,
    metadata: any,
    direction: any,
    custom_ingredient: any,
    default_quantity: any,
    product_highlights: any,
    product_url:any,
    country_code:any,
    token
  ) {
    const path = `${this.api_url}/product_custom/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        product_id: product_id,
        language_code: language_code,
        product_name: product_name,
        product_desc: product_desc,
        product_overview: product_overview,
        primary_photo: primary_photo,
        product_photo_list: imgURL_UploadImg,
        metadata: metadata,
        direction: direction,
        custom_ingredient: custom_ingredient,
        default_quantity: default_quantity,
        product_highlights: product_highlights,
        product_url: product_url,
        country_code: country_code,
      },
      { headers }
    );
  }

  updateCustomArrangement(body, token) {
    const path = `${this.api_url}/product_custom/arrangement/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updateProductMixtureInfo(
    store_id: any,
    product_id: any,
    language_code: any,
    product_name: any,
    token
  ) {
    const path = `${this.api_url}/product_custom/update_mixture`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        product_id: product_id,
        language_code: language_code,
        product_name: product_name,
      },
      { headers }
    );
  }

  resetProductCustom(token, product_id: any, language_code: any) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/product_custom/reset`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        product_id: product_id,
        language_code: language_code,
      },
      { headers }
    );
  }

  getProductCustomInfo(token, product_id: any, language_code: any, countryCode: string) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/product_custom/list?store_id=${store_id}&product_id=${product_id}&language_code=${language_code}&country_code=${countryCode}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getProductCustomArrangement(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/product_custom/arrangement?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  createProductCustom(
    store_id: any,
    language_code: any,
    product_name: any,
    product_desc: any,
    product_overview: any,
    primary_photo: any,
    token
  ) {
    const path = `${this.api_url}/product_custom/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        language_code: language_code,
        product_name: product_name,
        product_desc: product_desc,
        product_overview: product_overview,
        primary_photo: primary_photo,
      },
      { headers }
    );
  }

  getOrderHistoryList(
    token,
    page: any,
    limit: any,
    startDate: any,
    endDate: any
  ) {
    //getOrderHistoryList(token, Store_id: any, start_date:any, end_date:any, page:any ){
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/order/list?store_id=${store_id}&start_date=${startDate}&end_date=${endDate}&page=${page}&limit=${limit}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
    //const path = `${this.api_url}/order/list?store_id=1&start_date=${start_date}&end_date=${end_date}&page=${page}&limit=20`;
  }

  getSearchOrderHistory(token, o_id: any) {
    //getOrderHistoryList(token, Store_id: any, start_date:any, end_date:any, page:any ){
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/order/list?store_id=${store_id}&o_id=${o_id} `;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
    //const path = `${this.api_url}/order/list?store_id=1&start_date=${start_date}&end_date=${end_date}&page=${page}&limit=20`;
  }

  getStoreLanguage(token, store_id) {
    const path =
      `${this.api_url}/preference/language/list?store_id=` + store_id;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getStoreCountry(token, store_id) {
    const path = `${this.api_url}/preference/country/list?store_id=` + store_id;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  /*getOrderList(token){
    const path = `${this.api_url}/order/list?store_id=1&page=1`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }*/

  getDealerInfo(token) {
    const path = `${this.api_url}/dealer/info`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateDealerInfo(
    token,
    dealer_id: any,
    dealer_name: any,
    dealer_addresses: any,
    dealer_phone: any,
    dealer_email: any,
    dealer_company_name: any,
    dealer_company_addresses: any,
    dealer_company_phone: any,
    dealer_company_email: any
  ) {
    const path = `${this.api_url}/dealer/update`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.post(
      path,
      {
        d_id: dealer_id,
        dealer_name: dealer_name,
        dealer_addresses: dealer_addresses,
        dealer_phone: dealer_phone,
        dealer_email: dealer_email,
        dealer_company_name: dealer_company_name,
        dealer_company_addresses: dealer_company_addresses,
        dealer_company_phone: dealer_company_phone,
        dealer_company_email: dealer_company_email,
      },
      { headers }
    );
  }

  getDiscountList(store_id: any, token) {
    const path = `${this.api_url}/discount/list?store_id=${store_id}&excluded=1`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getStoreDiscount(store_id: any, token) {
    const path = `${this.api_url}/discount/list?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getStorePromotion(store_id: any, token) {
    const path = `${this.api_url}/promotion/list?store_id=${store_id}&excluded=1`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getPromotionList(store_id: any, token) {
    const path = `${this.api_url}/promotion/list?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  // Active, in-date promotions only. `excluded` picks the side of the store's
  // promotion config the endpoint returns: true = the promotions added to the
  // store, false = every other one. The checkout page applies both sides, so
  // a caller that needs the whole list asks twice and merges.
  getActivePromotions(store_id: any, token, excluded: boolean) {
    const path = `${this.api_url}/promotion/list?store_id=${store_id}&status=1&valid=1${
      excluded ? '&excluded=1' : ''
    }`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  addStorePromotion(
    store_id: any,
    promotion_id: any,
    promotion_config: any,
    token
  ) {
    const path = `${this.api_url}/promotion/add_promotion`;

    const headers = new HttpHeaders().set('Authorization', token);
    const formData = new FormData();

    formData.append('store_id', store_id);
    formData.append('promotion_id', promotion_id);
    formData.append('promotion_config', promotion_config);

    return this.customHttpClient.post(path, formData, { headers });
  }

  deleteStorePromotion(store_id: any, p_id: any, token) {
    const path = `${this.api_url}/promotion/delete_promotion?store_id=${store_id}&promotion_id=${p_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id: store_id, p_id: p_id },
      { headers }
    );
  }

  updateStoreDiscount(params, token) {
    const path = `${this.api_url}/discount/set`;
    const formData = new FormData();

    formData.append('store_id', params.store_id);
    formData.append('voucher_id', params.discount_id);

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, formData, { headers });
  }

  getTotalSalesByDealer(token, dealerId) {
    const path = `${this.api_url}/report/dealer/sales?dealer_id=${dealerId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getOrderSummary(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/store/summary?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getSaleChart(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/store/chart?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getRecentOrder(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/recent_order?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getWeekOrders(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/store/week?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getCustomerRate(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/store/customer_rate?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getTotalSale(store_id: any, country_code, token) {
    const path = `${this.api_url}/report/total_sales?store_id=${store_id}&country_code=${country_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateStoreLanguage(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/preference/language/update?store_id=${store_id}&language_code=zh-TW&status=1&default=1`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, { headers });
  }

  getMediaList(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/media/list?store_id=${store_id}&is_default=0`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  createMedia(
    store_id: any,
    title: any,
    caption: any,
    alt_text: any,
    filesize: any,
    photo: any,
    token
  ) {
    const path = `${this.api_url}/media/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        title: title,
        caption: caption,
        alt_text: alt_text,
        filesize: filesize,
        photo: photo,
      },
      { headers }
    );
  }

  getMediaUploadEndpoint(store_id: any, file: File, token) {
    const path = `${this.api_url}/media/upload/endpoint`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id: store_id, name: file.name, size: file.size, type: file.type },
      { headers }
    );
  }

  completeMediaUpload(
    store_id: any,
    upload_token: string,
    parts: { PartNumber: number; ETag: string }[],
    title: any,
    caption: any,
    alt_text: any,
    token
  ) {
    const path = `${this.api_url}/media/upload/complete`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id, upload_token, parts, title, caption, alt_text },
      { headers }
    );
  }

  createMediaImg(store_id: any, photo: any, token) {
    const path = `${this.api_url}/media/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id: store_id, photo: photo },
      { headers }
    );
  }

  updateMedia(
    store_id: any,
    media_id: any,
    title: any,
    caption: any,
    alt_text: any,
    filesize: any,
    photo: any,
    token
  ) {
    const path = `${this.api_url}/media/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        store_id: store_id,
        media_id: media_id,
        title: title,
        caption: caption,
        alt_text: alt_text,
        filesize: filesize,
        photo: photo,
      },
      { headers }
    );
  }

  GetCustomer(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/customer/list_customer?store_id=` + store_id;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  removeMedia(store_id: any, media_id: any, token) {
    const path = `${this.api_url}/media/remove`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id: store_id, media_id: media_id },
      { headers }
    );
  }

  updateCustomContent(body, token) {
    const path = `${this.api_url}/custom_content/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  getCustomContent(store_id, lang_code = 'en-US', theme_id = '', token = '') {
    const path = `${this.api_url}/custom_content/info?theme_id=${theme_id}&language_code=${lang_code}&store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getThemePresetList(token) {
    const path = `${this.api_url}/theme/preset/list`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getThemePresetDetails(preset_id, token) {
    const path = `${this.api_url}/theme/preset/details?preset_id=${preset_id}`;
    console.log({ path });

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  selectTheme(preset_id, token) {
    const path = `${this.api_url}/estore/update`;
    const headers = new HttpHeaders().set('Authorization', token);

    const store_id = localStorage.getItem('store_id');

    const params = {
      theme_id: preset_id,
      store_id: store_id,
    };

    return this.customHttpClient.post(path, params, { headers });
  }

  // getTheme(theme_id, token) {
  //   const path = `${this.api_url}/theme/list?theme_id=${theme_id}`;

  //   const headers = new HttpHeaders().set('Authorization', token);

  //   return this.customHttpClient.get(path, { headers });
  // }

  getSchema(theme_id, token) {
    const path = `${this.api_url}/theme/preset/details?preset_id=${theme_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getEmailTemplates(token) {   // getEmailTemplates(token, store_id)
    // const path = `${this.api_url}/email_template/list?store_id=${store_id}`;
    const path = `${this.api_url}/email_template/list_template`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getEmailTemplatebyId(token, template_id) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/email_template/info?store_id=${store_id}&template_id=${template_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateEmailTemplate(params, token) {
    const path = `${this.api_url}/email_template/update`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, params, { headers });
  }

  duplicateEmailTemplate(params, token) {
    const path = `${this.api_url}/email_template/duplicate`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, params, { headers });
  }

  revertEmailTemplate(params, token) {
    const path = `${this.api_url}/email_template/revert`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, params, { headers });
  }

  getNotification(token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/notification/list?store_id=${store_id}&indicator=0`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateMarkAsRead(notification_id: any, store_id: any, is_read: any, token) {
    const path = `${this.api_url}/notification//markall`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      {
        notification_id: notification_id,
        store_id: store_id,
        is_read: is_read,
      },
      { headers }
    );
  }

  updateCodStatus(body, token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/order/approve`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  cancelCodStatus(body, token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/order/cancel`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  cancelSubscription(body, token) {
    const path = `${this.api_url2}/gateway/2c2p/cancel`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getGlobalSEO(token, store_id, language_code: any) {
    const path = `${this.api_url}/estore/seo/info?parameter=${store_id}&store_id=${store_id}&page_type=global&language=${language_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  updateSeoConfig(token, body) {
    const path = `${this.api_url}/estore/seo/save`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getCheckoutLinkLists(token, store_id) {
    const path = `${this.api_url}/checkout_link/list?store_id=${store_id}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  fetchExpressCheckoutBySlug(token, store_id, slug, countryCode) {
    const path = `${this.api_url}/checkout_link/get_by_slug`;
    const params = {
      slug: slug,
      store_id: store_id,
      country_code: countryCode,
    };

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers, params });
  }

  updateCheckoutLink(token, body) {
    const path = `${this.api_url}/checkout_link/update`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  createCustomPage(body, token) {
    const path = `${this.api_url}/page/create`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  saveHtmlPage(body, token) {
    const path = `${this.api_url}/page/save-html`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  /**
   * The page currently serving the store's home route, or null data when the
   * storefront still shows its built-in home page. Only one page per store can
   * hold it, so this is what a new home page would replace.
   */
  getHomePage(token, storeId) {
    const path = `${this.api_url}/page/home_page?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  /**
   * Change the state of the store's home page. `is_home` gives the storefront's
   * home route to a page or takes it away; `visibility` publishes or unpublishes
   * the page that holds it, which is how the route is turned off without giving
   * the page up. Pass only what should change — the page itself is left alone.
   */
  setHomePageState(
    token,
    storeId,
    storePageId,
    changes: { is_home?: boolean; visibility?: number; country_code?: string }
  ) {
    const path = `${this.api_url}/page/home_page`;
    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(
      path,
      { store_id: storeId, store_page_id: storePageId, ...changes },
      { headers }
    );
  }

  validatePageUrl(pageUrl) {
    const storeId = localStorage.getItem('store_id');
    const path = `${this.api_url}/page/validate_url?page_url=${pageUrl}&store_id=${storeId}`;

    return this.customHttpClient.get(path, {});
  }

  removeCheckoutLink(token, body) {
    const path = `${this.api_url}/checkout_link/remove`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getCheckUrl(url, country, languageCode, token) {
    const store_id = localStorage.getItem('store_id');

    const path = `${this.api_url}/checkout_link/url_check?url=${url}&country_code=${country}&store_id=${store_id}&language=${languageCode}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  postGenerateLink(body, token) {
    const path = `${this.api_url}/checkout_link/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getNexusInfo(body, token) {
    const path = `${this.api_url}/nexus/info`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // SSO link that opens a nexus page directly. body: { store_id, destination }
  // where destination is e.g. 'smart_ads' (Create Ads, smart mode).
  getNexusSsoLink(body, token) {
    const path = `${this.api_url}/nexus/sso`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  // api_url already ends in /v1, so this resolves to /v1/domain/list_company_domain
  listCompanyDomain(body, token) {
    const path = `${this.api_url}/domain/list_company_domain`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  updateCustomPage(body, token) {
    const path = `${this.api_url}/page/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  getCustomPage({ store_page_id, store_id }, token) {
    const path = `${this.api_url}/page/info`;

    const headers = new HttpHeaders().set('Authorization', token);

    const params = {
      store_page_id: store_page_id,
      store_id: store_id,
    };

    return this.customHttpClient.get(path, { headers, params });
  }

  savePreview(preview_content, preview_type, token) {
    const store_id = localStorage.getItem('store_id');
    const path = `${this.api_url}/preview/save_preview`;

    const headers = new HttpHeaders().set('Authorization', token);

    const body = {
      preview_content: preview_content,
      preview_type: preview_type,
      store_id: store_id,
    };

    return this.customHttpClient.put(path, body, { headers });
  }

  getPageList(token, store_id) {
    const path = `${this.api_url}/page/list?store_id=${store_id}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  updatePage(token, body) {
    const path = `${this.api_url}/page/update`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, body, { headers });
  }

  getPreviewToken(token, store_id, page) {
    const path = `${this.api_url}/preview/get_token?store_id=${store_id}&path=${page}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getMenuList(token, store_id) {
    const path = `${this.api_url}/menu_manager/list?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  putMenuUpdate(token, body) {
    const path = `${this.api_url}/menu_manager/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  updateMenuCustomArrangement(token, body) {
    const path = `${this.api_url}/menu_manager/custom_arrangement`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  postMenuCreate(token, body) {
    const path = `${this.api_url}/menu_manager/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getExpressDashboardRate(token, store_id) {
    const path = `${this.api_url}/checkout_link/rate?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getExpressDashboardAnalysisData(token, store_id, start_date, end_date) {
    const path = `${this.api_url}/checkout_link/trend?store_id=${store_id}&start_date=${start_date}&end_date=${end_date}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getExpressDashboardPopular(token, store_id) {
    const path = `${this.api_url}/checkout_link/popular?store_id=${store_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  postDuplicatePage(token, body) {
    const path = `${this.api_url}/page/duplicate`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getReferralLinks(token, storeId) {
    const path = `${this.api_url}/program/referral/link?store_id=${storeId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getAffiliateList(token, storeId) {
    const path = `${this.api_url}/program/referral/list_affiliate?store_id=${storeId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  postCreateFaq(body, token) {
    const path = `${this.api_url}/product_faq/create`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  postUpdateFaq(body, token) {
    const path = `${this.api_url}/product_faq/update`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.put(path, body, { headers });
  }

  postDeleteFaq(faq_id: any, token) {
    const path = `${this.api_url}/product_faq/delete?id=${faq_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, { id: faq_id }, { headers });
  }

  getProductFaqLists(token, store_id: any, language_code: any) {
    const path = `${this.api_url}/product_faq/list?store_id=${store_id}&language_code=${language_code}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getProductFaqById(token, id: any) {
    const path = `${this.api_url}/product_faq/faq?id=${id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getDealerCredit(token, dealerId: any, configKey?: string) {
    let path = `${this.api_url}/dealer/credit?dealer_id=${dealerId}`;
    if (configKey) {
      path += `&config_key=${encodeURIComponent(configKey)}`;
    }

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  postDuplicateFaq(faq_id: any, token) {
    const path = `${this.api_url}/product_faq/duplicate?id=${faq_id}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, { id: faq_id }, { headers });
  }

  putFaqPosition(token, body: any) {
    const path = `${this.api_url}/product_faq/update_position`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getPageThemeList(countryCode = '') {
    const token = localStorage.getItem('bearer');
    let path = `${this.api_url}/page/theme/list?country_code=` + countryCode;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  /**
   * List Studio-editor starter templates: store_page_theme rows with status=2,
   * fetched via the page_theme_type=new filter. Each row includes its
   * theme_template_json (the Studio section array) so the picker can seed the
   * editor without a second request. See PageManageComponent.loadStudioTemplates.
   */
  getStudioPageThemeList(countryCode = '') {
    const token = localStorage.getItem('bearer');
    const path =
      `${this.api_url}/page/theme/list?country_code=${countryCode}&page_theme_type=new`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  /**
   * List HTML-editor starter templates from the backend.
   *
   * Default (customize-myself flow): normal (type='general') store_page_theme rows
   * tagged theme_categories_id=5, filtered to category 5 client-side
   * (see PageManageComponent.isHtmlEditorTheme).
   *
   * Product flow: pass type='product' — the endpoint filters store_page_theme.type
   * server-side (getAllTheme() applies extra query params as WHERE clauses), returning
   * only the product HTML templates.
   *
   * grouped=true collapses templates that share a store_page_theme.group into one
   * representative row carrying group_details {code,name,description,theme_ids} — used
   * by the product picker so a group renders as a single card.
   */
  getHtmlPageThemeList(type: string = null, grouped = false) {
    const token = localStorage.getItem('bearer');
    let path = `${this.api_url}/page/theme/list?country_code=ALL`;
    if (type) {
      path += `&type=${encodeURIComponent(type)}`;
    }
    if (grouped) {
      path += `&grouped=true`;
    }

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getPageTheme(
    store_id: any,
    theme_id: any = 0,
    language_code = '',
    product = null,
    country_code = '',
    disable_ai_generate = null
  ) {
    const token = localStorage.getItem('bearer');
    const path = `${this.api_url}/page/theme/get`;

    const params = {
      store_id,
      theme_id,
      language_code,
      country_code,
      disable_ai_generate,
    };
    if (product) params['content_product_name'] = product;
    if (product) params['disable_ai_generate'] = disable_ai_generate;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers, params });
  }
  // Session cache of AI-generated product page themes (page/theme/get responses),
  // keyed by every request parameter that changes the generated output. Lets the
  // HTML editor skip the ~15s Flowise round-trip when the same product/template/
  // language/country combination is requested again. In-memory only: a refresh
  // clears it, so stale generations don't outlive the SPA session.
  private generatedPageThemeCache = new Map<string, any>();
  private readonly GENERATED_PAGE_THEME_CACHE_MAX = 10;

  private generatedPageThemeKey(
    store_id: any,
    theme_id: any,
    language_code: string,
    product: string,
    country_code: string,
  ) {
    return [store_id, theme_id, language_code, product, country_code].join('|');
  }

  /** Cached page/theme/get response for these generation params, or null. */
  getCachedGeneratedPageTheme(
    store_id: any,
    theme_id: any,
    language_code: string,
    product: string,
    country_code: string,
  ) {
    return (
      this.generatedPageThemeCache.get(
        this.generatedPageThemeKey(
          store_id,
          theme_id,
          language_code,
          product,
          country_code,
        ),
      ) ?? null
    );
  }

  /**
   * Store a successful page/theme/get response for reuse. Callers should skip
   * fallback responses (ai_generated === false) so a retry can regenerate.
   */
  cacheGeneratedPageTheme(
    store_id: any,
    theme_id: any,
    language_code: string,
    product: string,
    country_code: string,
    response: any,
  ) {
    const key = this.generatedPageThemeKey(
      store_id,
      theme_id,
      language_code,
      product,
      country_code,
    );
    // Re-insert so Map iteration order doubles as least-recently-written order.
    this.generatedPageThemeCache.delete(key);
    this.generatedPageThemeCache.set(key, response);
    while (
      this.generatedPageThemeCache.size > this.GENERATED_PAGE_THEME_CACHE_MAX
    ) {
      this.generatedPageThemeCache.delete(
        this.generatedPageThemeCache.keys().next().value,
      );
    }
  }

  /**
   * Fetch a theme's stored HTML verbatim (theme_template_json.content) with no AI
   * generation — unlike getPageTheme(), which runs product templates through Flowise.
   *
   * Serves the HTML editor's Style switcher: the caller keeps the body it already
   * generated and only takes the new style's shell, so there's nothing to regenerate.
   */
  getPageThemeContent(theme_id: any) {
    const token = localStorage.getItem('bearer');
    const path = `${this.api_url}/page/theme/content`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, {
      headers,
      params: { theme_id },
    });
  }

  getProductSeriesList(countryCode = '') {
    const token = localStorage.getItem('bearer');
    const store_id = localStorage.getItem('store_id');

    const path =
      `${this.api_url}/product/series/list?country_code=` + countryCode;

    const params = { store_id };

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers, params });
  }
  getProductNameBySeries(countryCode = '') {
    const token = localStorage.getItem('bearer');
    const store_id = localStorage.getItem('store_id');

    const path =
      `${this.api_url}/product/series/group?country_code=` + countryCode;

    const params = { store_id };

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers, params });
  }

  getReferralStatus(token) {
    const path = `${this.api_url}/program/referral/status`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getProgramInfo(token, storeId) {
    const path = `${this.api_url}/program/info?store_id=${storeId}`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getPageThemeCategories(token) {
    const path = `${this.api_url}/page/theme/categories`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.get(path, { headers });
  }

  getAbandonedCarts(token, store_id, email, date, page, limit) {
    const path = `${this.api_url}/cart/list?store_id=${store_id}&date=${date}&email=${email}&page=${page}&limit=${limit}&status=0`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  createNewCustomPage(token, body: any) {
    const path = `${this.api_url}/page/theme/get_new`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }


  saveNewCustomPage(token, body: any) {
    const path = `${this.api_url}/template/site/create_from_json`;

    const headers = new HttpHeaders().set('Authorization', token);

    return this.customHttpClient.post(path, body, { headers });
  }

  getReplayVisitors(token, storeId, page = 1, limit = 20, startDate?: string, endDate?: string) {
    let path = `${this.api_url2}/v1/tools/replay/visitors?store_id=${storeId}&page=${page}&limit=${limit}`;
    if (startDate) path += `&start_date=${startDate}`;
    if (endDate) path += `&end_date=${endDate}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getReplaySessions(token, storeId, visitorId = '', page = 1, limit = 20, startDate?: string, endDate?: string, pageFilter?: string) {
    let path = `${this.api_url2}/v1/tools/replay/sessions?store_id=${storeId}&current_page=${page}&limit=${limit}`;
    if (visitorId) path += `&visitor_id=${visitorId}`;
    if (startDate) path += `&start_date=${startDate}`;
    if (endDate) path += `&end_date=${endDate}`;
    if (pageFilter) path += `&page_filter=${pageFilter}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  // store_id is required: the API only plays sessions of the caller's own store.
  getReplayEvents(token, sessionId, storeId) {
    const path = `${this.api_url2}/v1/tools/replay/session/${sessionId}?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  // Events of the whole visit around a session: the visitor's consecutive
  // page sessions merged into one replay, plus the list of those sessions.
  getReplayVisitEvents(token, sessionId, storeId) {
    const path = `${this.api_url2}/v1/tools/replay/visit/${sessionId}?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getAnalyticsSessions(token, storeId, page = 1, limit = 20, startDate?: string, endDate?: string) {
    let path = `${this.api_url2}/v1/tools/analytics/sessions?store_id=${storeId}&current_page=${page}&limit=${limit}`;
    if (startDate) path += `&start_date=${startDate}`;
    if (endDate) path += `&end_date=${endDate}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getAnalyticsSession(token, storeId, sessionId: string) {
    const path = `${this.api_url2}/v1/tools/analytics/session/${sessionId}?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getProfitLostReport(token, storeId, startDate?: string, endDate?: string) {
    let path = `${this.api_url}/report/profit_lost?store_id=${storeId}`;
    if (startDate) path += `&start_date=${startDate}`;
    if (endDate) path += `&end_date=${endDate}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getLandingPageReport(token, storeId) {
    const path = `${this.api_url}/report/landing_page_performance?store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  compareLandingPage(token: string, storeId: string, ownPageUrl: string, competitorUrl: string) {
    const path = `${this.api_url}/ai/landing_page/compare`;
    const headers = new HttpHeaders().set('Authorization', token);
    const body = { store_id: storeId, own_page_url: ownPageUrl, competitor_url: competitorUrl };
    return this.customHttpClient.post(path, body, { headers });
  }

  manualApplyPlanItem(token: string, comparisonId: string, day: number, storeId: string) {
    const path = `${this.api_url}/ai/landing_page/manual_apply`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, { comparison_id: comparisonId, day, store_id: storeId }, { headers });
  }

  updateActionPlan(token: string, comparisonId: string, day: number, fields: Record<string, any>) {
    const path = `${this.api_url}/ai/landing_page/update_action_plan`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, { comparison_id: comparisonId, day, ...fields }, { headers });
  }

  updateActionPlanStatus(token: string, comparisonId: string, status: number, storeId: string) {
    const path = `${this.api_url}/ai/landing_page/update_action_plan_status`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, { comparison_id: comparisonId, status, store_id: storeId }, { headers });
  }

  retryDay(token: string, comparisonId: string, storeId: string, day: number) {
    const path = `${this.api_url}/ai/landing_page/retry_day`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, { comparison_id: comparisonId, store_id: +storeId, day }, { headers });
  }

  applyLandingPageUpdate(token: string, comparisonId: string, storeId: string, apply: number = 1) {
    const path = `${this.api_url}/ai/landing_page/update?id=${comparisonId}&store_id=${storeId}&apply=${apply}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.put(path, {}, { headers });
  }

  getLandingPageList(token: string, storeId: string, limit = 10, offset = 0) {
    const path = `${this.api_url}/ai/landing_page/list?store_id=${storeId}&limit=${limit}&offset=${offset}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }

  getLandingPageDetail(token: string, id: string, storeId: string) {
    const path = `${this.api_url}/ai/landing_page/detail?id=${id}&store_id=${storeId}`;
    const headers = new HttpHeaders().set('Authorization', token);
    return this.customHttpClient.get(path, { headers });
  }
}
